import { Button, Dialog, DialogActions, Box, DialogContent, IconButton, DialogTitle, Typography, FormHelperText, Select, MenuItem, TextField, InputAdornment, FormControl, Alert, Divider } from '@mui/material';
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { CustomSingleSelect } from '../../utils/CustomSingleSelect';
import { CustomErrorLogger } from '../../utils/CustomErrorLogger';
import { CustomSwitch } from '../../utils/CustomSwitch';
import uploadIcon from '../../images/upload.svg';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useFormik } from 'formik';
import Iconify from '../Iconify';
import * as Yup from 'yup';
import axios from 'axios';
import '../../css/contracts/addContractDialog.css';

export const AddContractDialoge = ({ fromMachine, machine_ids, openAddContract, setOpenAddContract, setOpenSuccessfulAddContract, setContractId, contracts, setContracts, totalContracts, setTotalContracts, filteredContractsCount, setFilteredContractsCount }) => {

    const { t } = useTranslation();
    const navigate = useNavigate();

    const [errMsg, setErrMsg] = useState(null);
    const [errDate, setErrDate] = useState(null);
    const [documents, setDocuments] = useState([]);

    const fileInputRef = useRef(null);

    const formik = useFormik({

        initialValues: {
            title: '',
            contract_number: '',
            company: '',
            start_date: '',
            end_date: '',
            visit_count: '',
            visit_frequency: 'year',
            auto_renew: false,
            rappel_frequency: 0,
            documents: [],
            machines: machine_ids || [],
        },

        validationSchema: Yup.object().shape({
            title: Yup.string().required(t('isRequired')),
            contract_number: Yup.string().optional(),
            company: Yup.object().required(t('isRequired')),
            start_date: Yup.date().required(t('isRequired')),
            end_date: Yup.date().optional(),
            visit_count: Yup.number().optional(),
            visit_frequency: Yup.string().optional(),
            auto_renew: Yup.boolean().required(),
            documents: Yup.array().optional(),
        }),

        onSubmit: async (values) => {
            try {
                // Create FormData object
                const formData = new FormData();

                // Append files (documents) to FormData
                documents.forEach((file) => {
                    formData.append('files', file.file);
                });

                // Append each contract data field individually
                formData.append('title', values.title.trim());
                formData.append('contract_number', values.contract_number.trim());
                formData.append('company_id', values.company.value);
                formData.append('start_date', new Date(values.start_date).toISOString());

                if (values.end_date) {
                    formData.append('end_date', new Date(values.end_date).toISOString());
                }
                if (values.visit_count) {
                    formData.append('visit_count', Number(values.visit_count));
                }

                if (values.visit_frequency) {
                    formData.append('visit_frequency', values.visit_frequency);
                }

                formData.append('auto_renew', Boolean(values.auto_renew));
                formData.append('rappel_frequency', values.rappel_frequency.toString());

                if (values.machines.length > 0) {
                    formData.append(
                        'machines',
                        JSON.stringify(values.machines.map(machine => machine.id || machine.value || machine))
                    );
                }

                // Send the FormData object via Axios
                const response = await axios.post('contract', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });

                if (response.status === 200 || response.status === 201) {
                    if (fromMachine) {
                        navigate(`/dashboard/contract/contract_details/${response.data.id}`)
                        return;
                    }
                    setOpenAddContract(false);
                    setOpenSuccessfulAddContract(true);
                    setContractId(response.data.id);
                    setContracts([...contracts, response.data]);
                    setTotalContracts(totalContracts + 1);
                    setFilteredContractsCount(filteredContractsCount + 1)
                }
            } catch (error) {
                console.error('Error adding contract:', error);
                CustomErrorLogger(error, 'Error adding contract in AddContractDialog');
                setErrMsg(error?.response?.data?.message || error.message || 'erreur lors de la création du contrat');
            }
        },
    });

    const { handleSubmit, values, handleChange, handleBlur, touched, errors, setFieldValue } = formik;


    const handleClose = useCallback(() => {
        setOpenAddContract(false);
    }, [setOpenAddContract]);

    const handleCreateCompany = useCallback(async (newOption) => {
        try {
            const response = await axios.post('company', { name: newOption.label });
            if (response.status === 200 || response.status === 201) {
                setFieldValue('company', { value: response.data.id, label: response.data.name });
            }
        } catch (error) {
            console.error('Error creating company in AddContractDialoge:', error);
            CustomErrorLogger(error, "Error creating company in AddContractDialoge");
        }
    }, [setFieldValue]);

    const fetchCompanies = useCallback(async (offset, limit, search) => {
        try {
            const response = await axios.get('company', {
                params: {
                    offset,
                    limit,
                    filters: { debouncedSearch: search },
                },
            });
            if (response.status === 200 || response.status === 201) {
                return response.data.companiesData.map(company => ({
                    value: company.id,
                    label: company.name,
                }));
            }
        } catch (error) {
            console.error('Error fetching companies:', error);
            CustomErrorLogger(error, "error fetching companies in AddContractDialoge");
        }
    }, []);

    const handleFrequencyUnitChange = (event) => {
        setFieldValue('visit_frequency', event.target.value);
    };

    const handleFrequencyShortCut = (number, frequency) => {
        setFieldValue('visit_count', number);
        setFieldValue('visit_frequency', frequency);
    };

    const handleBlurContractStartDate = () => {
        if (values.end_date && new Date(values.start_date) > new Date(values.end_date)) {
            setErrDate('La date de début de contrat doit être inférieure à la date de fin de contrat');
            setFieldValue('start_date', '');
        } else {
            setErrDate(null);
        }
    };

    const handleBlurContractEndDate = () => {
        if (new Date(values.end_date) < new Date(values.start_date)) {
            setErrDate('La date de fin de contrat doit être supérieure à la date de début de contrat');
            setFieldValue('end_date', '');
        } else {
            setErrDate(null);
        }
    };

    const handleUploadClick = () => fileInputRef.current.click();

    const handleUploadDocuments = (files) => {

        const newFiles = Array.from(files).map(file => ({
            name: file?.name,
            size: (file?.size / (1024 * 1024))?.toFixed(2), // Convert size to MB
            file: file
        }));
        setDocuments(prevDocuments => [...prevDocuments, ...newFiles]);
    };

    const handleRemoveDocument = (index) => {
        setDocuments(prevDocuments => prevDocuments?.filter((_, i) => i !== index));
    };

    useEffect(() => {
        if (!values.end_date) {
            setFieldValue('auto_renew', false);
            setFieldValue('rappel_frequency', 0);
        }
    }, [values.end_date, setFieldValue])

    return (
        <Dialog open={openAddContract} fullWidth maxWidth="md">
            <IconButton onClick={handleClose} className='addContract_icon_button'>
                <Iconify icon="material-symbols:close" className='addContract_close_icon' />
            </IconButton>
            <DialogTitle className='addContract_dialog_title'>{t('addContractDialogeTitle')}</DialogTitle>
            <Divider />
            {errMsg && <Alert severity="error">{errMsg}</Alert>}
            <form onSubmit={handleSubmit}>
                <DialogContent className='addContract_dialog_content'>
                    <Box className="addContract_title_number_box">
                        <Box className="addContract_helper_field_box">
                            <FormHelperText id="outlined-weight-helper-text" className="addContractInputTitle">Titre du contrat</FormHelperText>
                            <TextField
                                fullWidth
                                placeholder='Entrez le titre'
                                value={values.title}
                                className='addContract_InputField'
                                id="outlined-adornment-weight"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                name="title"
                                error={touched.title && Boolean(errors.title)}
                                helperText={touched.title && errors.title}
                                aria-describedby="outlined-weight-helper-text"
                            />
                        </Box>
                        <Box className="addContract_helper_field_box">
                            <FormHelperText id="outlined-weight-helper-text" className="addContractInputTitle">Numéro du contrat (optionnel)</FormHelperText>
                            <TextField
                                fullWidth
                                placeholder='Entrez le numero'
                                value={values.contract_number}
                                className='addContract_InputField'
                                id="outlined-adornment-weight"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                name="contract_number"
                                error={touched.contract_number && Boolean(errors.contract_number)}
                                helperText={touched.contract_number && errors.contract_number}
                                aria-describedby="outlined-weight-helper-text"
                            />
                        </Box>
                    </Box>
                    <Box className='addContract_helper_field_box'>
                        <FormHelperText id="outlined-weight-helper-text" className="addContractInputTitle">Entreprise associée au contrat</FormHelperText>
                        <CustomSingleSelect
                            placeholder='Sélectionnez une entreprise'
                            className='addInvoice_InputField'
                            required={true}
                            value={values.company}
                            fetchFunction={fetchCompanies}
                            backendSearchable={true}
                            onChange={(value) => setFieldValue('company', value)}
                            creatable={true}
                            createFunction={handleCreateCompany}
                            helperText={touched.company && errors.company}
                            error={touched.company && Boolean(errors.company)}
                        />
                    </Box>

                    {errDate && <Alert severity="warning">{errDate}</Alert>}

                    <Box className="addContract_title_number_box">
                        <Box className='addContract_helper_field_box'>
                            <FormHelperText id="outlined-weight-helper-text" className="addContractInputTitle">Date de début</FormHelperText>
                            <TextField
                                sx={{ width: '100%' }}
                                className={`addContract_InputField`}
                                id="outlined-adornment-weight"
                                type='date'
                                value={values.start_date}
                                onChange={(event) => setFieldValue('start_date', event.target.value)}
                                onBlur={(event) => {
                                    handleBlur(event);
                                    handleBlurContractStartDate(event);
                                }}
                                name="start_date"
                                aria-describedby="outlined-weight-helper-text"
                                error={touched.start_date && Boolean(errors.start_date)}
                                helperText={touched.start_date && errors.start_date}
                            />
                        </Box>
                        <Box className='addContract_helper_field_box'>
                            <FormHelperText id="outlined-weight-helper-text" className="addContractInputTitle">Date de fin (optionnel)</FormHelperText>
                            <TextField
                                sx={{ width: '100%' }}
                                className={`addContract_InputField`}
                                id="outlined-adornment-weight"
                                type='date'
                                value={values.end_date}
                                onChange={(event) => setFieldValue('end_date', event.target.value)}
                                onBlur={(event) => {
                                    handleBlur(event);
                                    handleBlurContractEndDate(event);
                                }}
                                name="end_date"
                                aria-describedby="outlined-weight-helper-text"
                                error={touched.end_date && Boolean(errors.end_date)}
                                helperText={touched.end_date && errors.end_date}
                            />
                        </Box>
                    </Box>
                    <Box className="addContract_title_number_box">
                        <Box className='addContract_helper_field_box'>
                            <FormHelperText id="outlined-weight-helper-text" className="addContractInputTitle">Suivi des visites (optionnel)</FormHelperText>
                            <TextField
                                fullWidth
                                placeholder='Indiquez la fréquence des visites '
                                value={values.visit_count}
                                className='addContract_InputField'
                                id="outlined-adornment-weight"
                                type='number'
                                onChange={(event) => setFieldValue('visit_count', event.target.value)}
                                onBlur={handleBlur}
                                name="contract_number"
                                error={touched.visit_count && Boolean(errors.visit_count)}
                                helperText={touched.visit_count && errors.visit_count}
                                aria-describedby="outlined-weight-helper-text"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Select
                                                value={values.visit_frequency}
                                                onChange={handleFrequencyUnitChange}
                                                displayEmpty
                                                sx={{ backgroundColor: '#F2F2F2' }}
                                            >
                                                <MenuItem value="year">ans</MenuItem>
                                                <MenuItem value="month">mois</MenuItem>
                                                <MenuItem value="week">semaine</MenuItem>
                                            </Select>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiInputBase-root': {
                                        paddingRight: '0px !important',
                                    },
                                }}
                            />
                            <Box className='addContract_frequency_shortcut_box'>
                                <Typography className='addContract_frequency_shortcut_item' onClick={() => handleFrequencyShortCut(3, 'month')}>3 mois</Typography>
                                <Typography className='addContract_frequency_shortcut_item' onClick={() => handleFrequencyShortCut(6, 'month')}>6 mois</Typography>
                                <Typography className='addContract_frequency_shortcut_item' onClick={() => handleFrequencyShortCut(1, 'year')}>1 an</Typography>
                            </Box>
                        </Box>
                        {values.end_date &&
                            <Box className='addContract_helper_field_box'>
                                <FormHelperText id="outlined-weight-helper-text" className="addContractInputTitle">Renouvellement automatique</FormHelperText>
                                <CustomSwitch
                                    checked={values.auto_renew}
                                    onChange={(e) => setFieldValue('auto_renew', e.target.checked)}
                                />
                                {values.auto_renew &&
                                    <Box className='addContract_helper_field_box'>
                                        <Box id="outlined-weight-helper-text" className="addContract_rappel_box">
                                            <Iconify className='addContract_rappel_icon' icon="lucide:clock-fading" />
                                            <Typography className="addContractInputTitle" sx={{ margin: '0 !important' }}>Délai de préavis de non renouvellement</Typography>
                                        </Box>
                                        <FormControl fullWidth margin="dense" className='addContract_InputField'>
                                            <Select
                                                value={values.rappel_frequency}
                                                onChange={(e) => setFieldValue('rappel_frequency', e.target.value)}
                                            >
                                                <MenuItem value={3}>3 mois avant</MenuItem>
                                                <MenuItem value={6}>6 mois avant</MenuItem>
                                                <MenuItem value={8}>8 mois avant</MenuItem>
                                                <MenuItem value={0}>Aucun</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Box>
                                }
                            </Box>
                        }
                    </Box>
                    <Box className='addContract_helper_field_box'>
                        <FormHelperText id="outlined-weight-helper-text" className="addContractInputTitle">Document du contrat (optionnel)</FormHelperText>
                        <Box className='modifierPhoto_upload_box' onClick={handleUploadClick}>
                            <img src={uploadIcon} alt="icon" className='modifierPhoto_upload_icons' loading='lazy' decoding='async' />
                            <Box>
                                <Typography className='modifierPhoto_uplaod_first_typography'>Glisser-déposez le fichier ici, ou Parcourir</Typography>
                                <Typography className='modifierPhoto_uplaod_second_typography'>Format PDF de moins de 25Mo</Typography>
                            </Box>
                        </Box>
                        <input
                            type="file"
                            accept=".pdf"
                            ref={fileInputRef}
                            multiple
                            style={{ display: "none" }}
                            onChange={event => handleUploadDocuments(event.target.files)}
                        />
                    </Box>
                    <Box className='addContract_documents_box'>
                        {documents.map((doc, index) => (
                            <Box
                                className='addContract_document_item'
                                key={index}
                            >
                                <Iconify className='addContract_pdf_icon' icon="bxs:file-pdf" />
                                <Typography className='addContract_doc_name'>{doc.name}</Typography>
                                <Typography className='addContract_doc_size'>{doc.size} MB</Typography>
                                <Iconify className='addContract_delete_icon' icon="ic:outline-delete" onClick={() => handleRemoveDocument(index)} />
                            </Box>
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions className='addContract_dialog_actions'>
                    <Button className='addContract_button_annuler' onClick={handleClose}>{t('Annuler')}</Button>
                    <Button className='addContract_button_valider' variant="contained" type="submit">{t('Valider')}</Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};